import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AlertTriangle, Loader2, RefreshCw } from "lucide-react";
import { api } from "@shared/routes";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";

type Ambiente = "producao" | "sandbox";
interface Tarifa { percentBps: number; fixedCents: number }
interface TabelaTaxas {
  credito: Array<Tarifa & { ateParcelas: number }>;
  debito: Tarifa;
  boleto: { fixedCents: number };
  pix: Tarifa;
  diasRecebimento: { credito: number; debito: number; boleto: number };
}
interface Taxas { payment: Record<string, Record<string, any>> }
interface EstadoTaxas {
  ambiente: Ambiente;
  fonte: "api" | "fallback" | null;
  valido: boolean;
  ultimaSincronizacao: string | null;
  validoAte: string | null;
  promocaoExpiraEm: string | null;
  proximaExecucao: string;
  tabela: TabelaTaxas | null;
  pixExterno: { competencia: string; franquia: number; usadas: number; gratuitasRestantes: number; tarifadas: number; tarifaCents: number; tarifaTotalCents: number };
  fallback: { versao: number; padrao: boolean; salvoEm: string | null; promocaoExpiraEm: string | null; taxas: Taxas };
  historico: Array<{ id: number; em: string; tipo: string; descricao: string }>;
}

const brl = (cents: number) => (cents / 100).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const pct = (bps: number) => `${(bps / 100).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
const quando = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo", dateStyle: "short", timeStyle: "short" }) : "—";
const dia = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" }) : "—";
const tarifa = (t: Tarifa) => (t.percentBps ? `${pct(t.percentBps)} + ${brl(t.fixedCents)}` : brl(t.fixedCents));
const prazo = (d: number) => `${d} ${d === 1 ? "dia" : "dias"}`;

const url = (ambiente: Ambiente) => `${api.asaasTaxas.obter.path}?ambiente=${ambiente}`;

/** apiRequest lança "400: {json}"; devolve a lista de problemas do servidor quando existir. */
function mensagemDeErro(err: Error): string {
  try {
    const corpo = JSON.parse(err.message.replace(/^\d+:\s*/, ""));
    if (Array.isArray(corpo.erros) && corpo.erros.length) return corpo.erros.map((e: any) => e.mensagem).join(" · ");
    if (corpo.message) return corpo.message;
  } catch {
    /* mensagem não é JSON */
  }
  return err.message;
}

// ── Formulário do fallback (campos principais; o restante da configuração é preservado) ──
const FAIXAS = [
  ["oneInstallment", "1x"],
  ["upToSixInstallments", "2–6x"],
  ["upToTwelveInstallments", "7–12x"],
  ["upToTwentyOneInstallments", "13–21x"],
] as const;

const texto = (n: unknown) => (n == null ? "" : String(n));
function paraForm(t: Taxas) {
  const { creditCard: cc, bankSlip: bol, debitCard: deb } = t.payment;
  return {
    opCred: texto(cc.operationValue),
    normal: FAIXAS.map(([k]) => texto(cc[`${k}Percentage`])),
    promo: FAIXAS.map(([k]) => texto(cc[`discount${k[0].toUpperCase()}${k.slice(1)}Percentage`])),
    promoAte: (cc.discountExpiration ?? bol.expirationDate ?? "").slice(0, 10),
    deb: texto(deb.defaultPercentage),
    opDeb: texto(deb.operationValue),
    bolN: texto(bol.defaultValue),
    bolP: texto(bol.discountValue),
    dCred: texto(cc.daysToReceive),
    dDeb: texto(deb.daysToReceive),
    dBol: texto(bol.daysToReceive),
  };
}
type Form = ReturnType<typeof paraForm>;

const num = (s: string) => (s.trim() === "" ? NaN : Number(s.replace(",", ".")));
const opc = (s: string) => (s.trim() === "" ? null : num(s));

function paraTaxas(base: Taxas, f: Form): Taxas {
  const t: Taxas = JSON.parse(JSON.stringify(base));
  const { creditCard: cc, bankSlip: bol, debitCard: deb } = t.payment;
  const exp = f.promoAte ? `${f.promoAte} 23:59:59` : null;
  cc.operationValue = num(f.opCred);
  FAIXAS.forEach(([k], i) => {
    cc[`${k}Percentage`] = num(f.normal[i]);
    cc[`discount${k[0].toUpperCase()}${k.slice(1)}Percentage`] = opc(f.promo[i]);
  });
  cc.discountExpiration = exp;
  cc.daysToReceive = num(f.dCred);
  deb.defaultPercentage = num(f.deb);
  deb.operationValue = num(f.opDeb);
  deb.daysToReceive = num(f.dDeb);
  bol.defaultValue = num(f.bolN);
  bol.discountValue = opc(f.bolP);
  bol.expirationDate = exp;
  bol.daysToReceive = num(f.dBol);
  return t;
}

function Campo({ id, label, value, onChange, sufixo }: { id: string; label: string; value: string; onChange: (v: string) => void; sufixo?: string }) {
  return (
    <div className="space-y-1">
      <Label htmlFor={id} className="text-xs text-muted-foreground">
        {label}
      </Label>
      <div className="flex items-center gap-1">
        <Input id={id} data-testid={id} type="number" inputMode="decimal" step="0.01" min="0" className="h-8 w-full min-w-0" value={value} onChange={(e) => onChange(e.target.value)} />
        {sufixo && <span className="text-xs text-muted-foreground">{sufixo}</span>}
      </div>
    </div>
  );
}

function FallbackForm({ estado, ambiente }: { estado: EstadoTaxas; ambiente: Ambiente }) {
  const { toast } = useToast();
  const [form, setForm] = useState<Form>(() => paraForm(estado.fallback.taxas));
  const [erro, setErro] = useState<string | null>(null);
  useEffect(() => setForm(paraForm(estado.fallback.taxas)), [estado.fallback.versao, ambiente]);
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm((p) => ({ ...p, [k]: v }));
  const setI = (k: "normal" | "promo", i: number, v: string) => set(k, form[k].map((x, j) => (j === i ? v : x)));

  const salvar = useMutation({
    mutationFn: async () => {
      const res = await apiRequest(api.asaasTaxas.fallback.method, api.asaasTaxas.fallback.path, {
        ambiente,
        taxas: paraTaxas(estado.fallback.taxas, form),
      });
      return (await res.json()) as EstadoTaxas;
    },
    onSuccess: (novo) => {
      setErro(null);
      queryClient.setQueryData(["asaas-taxas", ambiente], novo);
      toast({ title: `Fallback salvo (versão ${novo.fallback.versao})` });
    },
    onError: (e: Error) => setErro(mensagemDeErro(e)),
  });

  return (
    <Card className="border-none shadow-md">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Fallback manual</CardTitle>
        <p className="text-xs text-muted-foreground">
          {estado.fallback.padrao
            ? "Valores iniciais ainda não salvos. "
            : `Versão ${estado.fallback.versao}, salva em ${quando(estado.fallback.salvoEm)}. `}
          Cada salvamento cria uma nova versão; os valores sincronizados do Asaas nunca são alterados.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {FAIXAS.map(([, rotulo], i) => (
            <Campo key={`n${i}`} id={`fb-normal-${i}`} label={`Crédito ${rotulo}`} value={form.normal[i]} onChange={(v) => setI("normal", i, v)} sufixo="%" />
          ))}
          {FAIXAS.map(([, rotulo], i) => (
            <Campo key={`p${i}`} id={`fb-promo-${i}`} label={`Promo ${rotulo}`} value={form.promo[i]} onChange={(v) => setI("promo", i, v)} sufixo="%" />
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Campo id="fb-op-cred" label="Fixa crédito" value={form.opCred} onChange={(v) => set("opCred", v)} sufixo="R$" />
          <Campo id="fb-deb" label="Débito" value={form.deb} onChange={(v) => set("deb", v)} sufixo="%" />
          <Campo id="fb-op-deb" label="Fixa débito" value={form.opDeb} onChange={(v) => set("opDeb", v)} sufixo="R$" />
          <div className="space-y-1">
            <Label htmlFor="fb-promo-ate" className="text-xs text-muted-foreground">
              Promoção até
            </Label>
            <Input id="fb-promo-ate" data-testid="fb-promo-ate" type="date" max="2026-12-23" className="h-8" value={form.promoAte} onChange={(e) => set("promoAte", e.target.value)} />
          </div>
          <Campo id="fb-bol-n" label="Boleto" value={form.bolN} onChange={(v) => set("bolN", v)} sufixo="R$" />
          <Campo id="fb-bol-p" label="Boleto promo" value={form.bolP} onChange={(v) => set("bolP", v)} sufixo="R$" />
          <Campo id="fb-d-cred" label="Prazo crédito" value={form.dCred} onChange={(v) => set("dCred", v)} sufixo="dias" />
          <Campo id="fb-d-deb" label="Prazo débito" value={form.dDeb} onChange={(v) => set("dDeb", v)} sufixo="dias" />
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Campo id="fb-d-bol" label="Prazo boleto" value={form.dBol} onChange={(v) => set("dBol", v)} sufixo="dias" />
        </div>
        {erro && (
          <p role="alert" data-testid="fallback-erro" className="text-sm text-destructive">
            {erro}
          </p>
        )}
        <div className="flex justify-end">
          <Button data-testid="btn-salvar-fallback" onClick={() => salvar.mutate()} disabled={salvar.isPending}>
            {salvar.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Salvar nova versão
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export function TaxasAsaasPanel() {
  const { toast } = useToast();
  const [ambiente, setAmbiente] = useState<Ambiente>("producao");
  const chave = ["asaas-taxas", ambiente];

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: chave,
    queryFn: async () => (await (await apiRequest("GET", url(ambiente))).json()) as EstadoTaxas,
  });

  const sincronizar = useMutation({
    mutationFn: async () => (await (await apiRequest(api.asaasTaxas.sincronizar.method, api.asaasTaxas.sincronizar.path, { ambiente })).json()) as EstadoTaxas,
    onSuccess: (novo) => {
      queryClient.setQueryData(chave, novo);
      toast({ title: "Taxas sincronizadas com o Asaas" });
    },
    onError: (e: Error) => {
      toast({ title: "Falha ao sincronizar", description: mensagemDeErro(e), variant: "destructive" });
      refetch();
    },
  });

  const cabecalho = (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div>
        <h2 className="text-base font-semibold">Taxas Asaas</h2>
        <p className="text-xs text-muted-foreground">Taxas de cobrança, prazos de recebimento e franquia de PIX externo.</p>
      </div>
      <div className="flex items-center gap-2">
        <Select value={ambiente} onValueChange={(v) => setAmbiente(v as Ambiente)}>
          <SelectTrigger aria-label="Ambiente" data-testid="select-ambiente-taxas" className="h-9 w-36">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="producao">Produção</SelectItem>
            <SelectItem value="sandbox">Sandbox</SelectItem>
          </SelectContent>
        </Select>
        <Button data-testid="btn-sincronizar-taxas" onClick={() => sincronizar.mutate()} disabled={sincronizar.isPending || isLoading}>
          {sincronizar.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
          Sincronizar agora
        </Button>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="space-y-4" data-testid="taxas-carregando">
        {cabecalho}
        <Skeleton className="h-24 w-full rounded-xl" />
        <Skeleton className="h-56 w-full rounded-xl" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="space-y-4">
        {cabecalho}
        <Alert variant="destructive" data-testid="taxas-erro">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Não foi possível carregar as taxas</AlertTitle>
          <AlertDescription className="flex flex-wrap items-center gap-3">
            {error ? mensagemDeErro(error as Error) : "Resposta vazia."}
            <Button size="sm" variant="outline" onClick={() => refetch()}>
              Tentar de novo
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const t = data.tabela;
  const linhas: Array<[string, string, string]> = t
    ? [
        ...t.credito.map((f, i): [string, string, string] => [
          `Crédito ${["1x", "2–6x", "7–12x", "13–21x"][i] ?? `até ${f.ateParcelas}x`}`,
          tarifa(f),
          prazo(t.diasRecebimento.credito),
        ]),
        ["Débito", tarifa(t.debito), prazo(t.diasRecebimento.debito)],
        ["Boleto pago", brl(t.boleto.fixedCents), `${t.diasRecebimento.boleto} dia útil`],
        ["PIX recebido", tarifa(t.pix), "imediato"],
      ]
    : [];
  const px = data.pixExterno;

  return (
    <div className="space-y-4">
      {cabecalho}

      {data.fonte !== "api" || !data.valido ? (
        <Alert variant={data.fonte ? "default" : "destructive"} data-testid="taxas-banner-fallback">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>
            {data.fonte === "fallback"
              ? data.valido
                ? `Usando o fallback manual (versão ${data.fallback.versao})`
                : `Fallback manual vencido (versão ${data.fallback.versao})`
              : data.fonte === "api"
                ? "Taxas sincronizadas vencidas"
                : "Sem taxas vigentes"}
          </AlertTitle>
          <AlertDescription>
            {data.fonte === null
              ? "Sincronize com o Asaas ou salve um fallback manual. Novas cobranças ficam bloqueadas até haver taxas válidas."
              : "A sincronização com o Asaas não está confirmada nas últimas 6 horas. Sincronize para voltar a usar os valores oficiais."}
          </AlertDescription>
        </Alert>
      ) : null}

      <Card className="border-none shadow-md">
        <CardContent className="pt-6">
          <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm sm:grid-cols-5">
            <div>
              <dt className="text-xs text-muted-foreground">Fonte</dt>
              <dd data-testid="taxas-fonte">
                {data.fonte === "api" ? <Badge>API Asaas</Badge> : data.fonte === "fallback" ? <Badge variant="secondary">Fallback manual</Badge> : "—"}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">Última sincronização</dt>
              <dd data-testid="taxas-ultima-sync">{quando(data.ultimaSincronizacao)}</dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">Válido até</dt>
              <dd data-testid="taxas-valido-ate">{quando(data.validoAte)}</dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">Promoção até</dt>
              <dd data-testid="taxas-promocao">{dia(data.promocaoExpiraEm)}</dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">Próxima execução</dt>
              <dd>{quando(data.proximaExecucao)}</dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      <Card className="border-none shadow-md">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Faixas e prazos de recebimento</CardTitle>
        </CardHeader>
        <CardContent>
          {t ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="taxas-tabela">
                <thead>
                  <tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="py-2 pr-4 font-medium">Método</th>
                    <th className="py-2 pr-4 font-medium">Tarifa</th>
                    <th className="py-2 font-medium">Recebimento</th>
                  </tr>
                </thead>
                <tbody>
                  {linhas.map(([m, tf, p]) => (
                    <tr key={m} className="border-b last:border-0">
                      <td className="py-2 pr-4">{m}</td>
                      <td className="py-2 pr-4 tabular-nums">{tf}</td>
                      <td className="py-2 tabular-nums">{p}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground" data-testid="taxas-vazio">
              Nenhuma taxa disponível ainda neste ambiente.
            </p>
          )}
        </CardContent>
      </Card>

      <Card className="border-none shadow-md">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">PIX externo</CardTitle>
        </CardHeader>
        <CardContent className="text-sm" data-testid="taxas-pix-externo">
          <p>
            <span className="font-semibold tabular-nums">{px.usadas}</span> de {px.franquia} operações gratuitas usadas em{" "}
            {px.competencia.slice(5, 7)}/{px.competencia.slice(0, 4)}
            {" · "}
            {px.gratuitasRestantes} restantes.
          </p>
          <p className="text-muted-foreground">
            Tarifa de {brl(px.tarifaCents)} a partir da {px.franquia + 1}ª operação
            {px.tarifadas > 0 ? `; ${px.tarifadas} tarifada${px.tarifadas > 1 ? "s" : ""} (${brl(px.tarifaTotalCents)}).` : "."}
          </p>
        </CardContent>
      </Card>

      <FallbackForm estado={data} ambiente={ambiente} />

      <Card className="border-none shadow-md">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Histórico</CardTitle>
        </CardHeader>
        <CardContent>
          {data.historico.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="taxas-historico-vazio">
              Nenhuma sincronização ou alteração registrada ainda.
            </p>
          ) : (
            <ul className="divide-y text-sm" data-testid="taxas-historico">
              {data.historico.map((h) => (
                <li key={h.id} className="flex flex-col gap-0.5 py-2 sm:flex-row sm:gap-4">
                  <span className="shrink-0 tabular-nums text-xs text-muted-foreground sm:w-36">{quando(h.em)}</span>
                  <span>{h.descricao}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
